import { useCallback, useEffect, useState } from 'react';

interface VersionInfo {
  version: string;
  timestamp: string;
  buildNumber?: string;
  forceRefresh?: boolean;
  message?: string;
}

interface VersionCheckState {
  hasUpdate: boolean;
  latestVersion: string | null;
  isForced: boolean;
  updateMessage: string | null;
  checkVersion: () => Promise<void>;
  dismissUpdate: () => void;
  performUpdate: () => void;
}

const CURRENT_VERSION = process.env.REACT_APP_VERSION || 'unknown';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const INITIAL_CHECK_DELAY = 5 * 1000; // 5 seconds

export const useVersionCheck = (): VersionCheckState => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [isForced, setIsForced] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      // Add timestamp to prevent caching
      const response = await fetch(`/version.json?t=${Date.now()}`);

      if (!response.ok) {
        console.warn('Version check failed:', response.status);
        return;
      }

      const versionInfo: VersionInfo = await response.json();

      // Compare versions
      if (versionInfo.version !== CURRENT_VERSION) {
        setHasUpdate(true);
        setLatestVersion(versionInfo.version);
        setIsForced(versionInfo.forceRefresh || false);
        setUpdateMessage(versionInfo.message || null);
        setIsDismissed(false);

        // Development-only version update logging
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log(`Update available: ${CURRENT_VERSION} → ${versionInfo.version}`);
        }
      } else {
        // Version matches, clear any update state
        if (hasUpdate) {
          setHasUpdate(false);
          setLatestVersion(null);
          setIsForced(false);
          setUpdateMessage(null);
        }
      }
    } catch (error) {
      // Silently fail - don't break the app if version check fails
      // eslint-disable-next-line no-console
      console.debug('Version check failed:', error);
    }
  }, [hasUpdate]);

  const dismissUpdate = useCallback(() => {
    if (!isForced) {
      setIsDismissed(true);
    }
  }, [isForced]);

  const performUpdate = useCallback(() => {
    // Force a hard refresh to clear all caches
    window.location.reload();
  }, []);

  useEffect(() => {
    // Don't run version checks in development unless explicitly enabled
    if (
      process.env.NODE_ENV === 'development' &&
      !window.localStorage.getItem('enableVersionCheck')
    ) {
      // eslint-disable-next-line no-console
      console.debug(
        'Version checking disabled in development. To enable: localStorage.setItem("enableVersionCheck", "true")',
      );
      return;
    }

    // Initial check after a short delay
    const initialTimer = setTimeout(checkVersion, INITIAL_CHECK_DELAY);

    // Set up recurring checks
    const interval = setInterval(checkVersion, CHECK_INTERVAL);

    // Also check when the page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return {
    hasUpdate: hasUpdate && !isDismissed,
    latestVersion,
    isForced,
    updateMessage,
    checkVersion,
    dismissUpdate,
    performUpdate,
  };
};
